import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import * as R from 'ramda';
import React from 'react';
import { Form, Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  SpecialHoursInput,
  EMPTY_SPECIAL_HOURS_INPUT_ENTRY,
  SpecialHoursInputEntry,
} from 'common/components/weekly-schedules';
import { Typography as T } from 'common/typography';
import { telemetryRestaurantDetailsInteractionSaveSpecialHour } from 'modules/venue/telemetry';

import { hasOverlappingTimePairs } from './utils';
import {
  reportSpecialHoursDateModeChange,
  reportSpecialHoursEntryAdd,
  reportSpecialHoursEntryRemove,
} from '../../behavioral';

type Props = {
  entries: SpecialHoursInputEntry[];
  submitting: boolean;
  timezone: string;
  onCancel: () => void;
  onSubmit: ({ specialHours }: { specialHours: SpecialHoursInputEntry[] }) => void;
};

const Header = styled(T.Heading6)`
  color: ${cbSemanticCssVars.textPrimary};
  display: flex;
  justify-content: space-between;
  margin-block-end: 24px;
  flex-wrap: wrap;
  gap: 12px 8px;
`;

const FormButtons = styled.div`
  margin-inline-start: auto;
  margin-block: -6px; /** to not affect the header spacings */
`;

const CancelButton = styled(Button).attrs({ size: 'small', variant: 'secondary' })`
  margin-inline-end: 12px;
`;

const SaveButton = styled(Button).attrs({ size: 'small', variant: 'primary' })``;

const SpecialHoursForm = ({ entries, onCancel, onSubmit, submitting, timezone }: Props) => {
  const intl = useIntl();

  const t = (id: string) => intl.formatMessage({ id });

  const handleSubmit = ({ specialHours }: { specialHours: SpecialHoursInputEntry[] }) => {
    onSubmit({ specialHours });
    telemetryRestaurantDetailsInteractionSaveSpecialHour();
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{ specialHours: entries.length ? entries : [EMPTY_SPECIAL_HOURS_INPUT_ENTRY] }}
      initialValuesEqual={R.equals}
      validate={values => {
        const specialHoursErrors: { [key: string]: string } = {};
        for (let i = 0; i < values.specialHours.length; i += 1) {
          const entry = values.specialHours[i];
          if (!entry.closedAllDay && !entry.open24Hours && hasOverlappingTimePairs(entry)) {
            specialHoursErrors[i] = t('special-hours.times-overlap');
          }
        }
        return Object.keys(specialHoursErrors).length === 0 ? {} : { specialHours: specialHoursErrors };
      }}
    >
      {({ handleSubmit: formSubmit, pristine, valid }) => {
        return (
          <>
            <Header>
              <FormattedMessage id={'venue-details.special-hours'} />
              <FormButtons>
                <CancelButton onClick={onCancel} disabled={submitting}>
                  <FormattedMessage id={'state.cancel'} />
                </CancelButton>
                <SaveButton disabled={!valid || pristine || submitting} onClick={formSubmit}>
                  <FormattedMessage id={'state.save'} />
                </SaveButton>
              </FormButtons>
            </Header>
            <Field<SpecialHoursInputEntry[]> name="specialHours" isEqual={R.equals}>
              {({ input, meta }) => (
                <SpecialHoursInput
                  entryErrors={meta.error}
                  timezone={timezone}
                  value={input.value}
                  onAddEntry={() => {
                    reportSpecialHoursEntryAdd();
                  }}
                  onChange={input.onChange}
                  onDatePickerModeChange={newMode => {
                    reportSpecialHoursDateModeChange(newMode);
                  }}
                  onRemoveEntry={() => {
                    reportSpecialHoursEntryRemove();
                  }}
                />
              )}
            </Field>
          </>
        );
      }}
    </Form>
  );
};

export default SpecialHoursForm;
